import { useEffect, useMemo, useState } from "react";
import { poolApi } from "../../services/businessApi";
import { vocabularyFor, weightLabel } from "../../services/productVocabulary";
import { formatDateOnly } from "../../services/hiveActions";
import { Card } from "../common/Card";
import { Badge } from "../common/Badge";
import { DataTable } from "../common/DataTable";
import { Field } from "../common/Field";
import { ListBound } from "../common/ListBound";

/**
 * ONE Recent Activity table for every other product (#518).
 *
 * Replaces the per-card history panes (#495). The Honey Pool has a single
 * History table at the bottom of its page; wax, propolis and pollen now do
 * the same, with a Product column and filters, because a card-by-card
 * history made "what did I do this week" a three-place search.
 *
 * ── ONE CALL PER PRODUCT, MERGED HERE ──
 *
 * `/honey/pool/movements` already filters by product_type (#495), and an
 * account has at most three products on this page, so three small calls
 * merged by date are cheaper than a new endpoint to keep in step.
 * `capped_at` is kept per product: if ANY product's log was cut short the
 * bound note shows, because a merged list that silently hides older rows
 * is the #372 complaint again.
 *
 * Only pool movements are listed (added, packaged, corrected, sold in bulk,
 * lost). Each response is still filtered to its own product, since a stale
 * API build returns everything and a row under the wrong product name is
 * worse than a missing one.
 */
const TYPE_TONE = {
  Harvest: "success",
  Bottling: "info",
  Correction: "warning",
  Sale: "neutral",
  Loss: "danger",
};

/* The product's own word for packaging (Pour / Pack / Bottle), so the What
   badge on a wax row does not say "Bottling". */
function whatLabel(m) {
  if (m.movement_type === "Bottling") return vocabularyFor(m.product_type).verb;
  if (m.movement_type === "Harvest") return "Added";
  return m.movement_type;
}

function detail(m) {
  const vocab = vocabularyFor(m.product_type);
  if (m.movement_type === "Bottling") {
    return `${m.bottle_count} x ${m.bottle_size_label || vocab.units}`;
  }
  const amount = weightLabel(m.product_type, Math.abs(Number(m.pounds_delta)));
  if (m.movement_type === "Correction") {
    return `${Number(m.pounds_delta) < 0 ? "Down" : "Up"} ${amount}`;
  }
  return amount;
}

export function ProductActivity({ productTypes, refreshKey }) {
  const [rows, setRows] = useState(null);
  const [failed, setFailed] = useState(false);
  const [cappedAt, setCappedAt] = useState(null);
  const [product, setProduct] = useState("");
  const [type, setType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const typesKey = productTypes.join("|");
  useEffect(() => {
    let live = true;
    setFailed(false);
    Promise.all(typesKey.split("|").filter(Boolean).map((t) =>
      poolApi.movements(t).then((r) => ({
        moves: (r.movements || [])
          .filter((m) => !m.product_type || m.product_type === t)
          .map((m) => ({ ...m, product_type: t })),
        capped: r.capped_at ?? null,
      }))))
      .then((all) => {
        if (!live) return;
        setRows(all.flatMap((a) => a.moves)
          .sort((a, b) => new Date(b.created_date) - new Date(a.created_date)));
        setCappedAt(all.map((a) => a.capped).find(Boolean) ?? null);
      })
      /* null stays null on failure: an empty list would claim nothing ever
         happened, and that is false when the request did not arrive. */
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, [typesKey, refreshKey]);

  const shown = useMemo(() => (rows || []).filter((m) => {
    if (product && m.product_type !== product) return false;
    if (type && m.movement_type !== type) return false;
    const day = String(m.created_date || "").slice(0, 10);
    if (from && day < from) return false;
    if (to && day > to) return false;
    return true;
  }), [rows, product, type, from, to]);

  return (
    <Card title="Recent Activity" className="section-spacing">
      <div style={{ display: "flex", gap: "var(--space-3)", flexWrap: "wrap",
                    marginBottom: "var(--space-3)" }}>
        <Field label="Product">
          {(id, aria) => (
            <select id={id} {...aria} className="touch-target" value={product}
              onChange={(e) => setProduct(e.target.value)}>
              <option value="">All products</option>
              {productTypes.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          )}
        </Field>
        <Field label="Activity">
          {(id, aria) => (
            <select id={id} {...aria} className="touch-target" value={type}
              onChange={(e) => setType(e.target.value)}>
              <option value="">All activity</option>
              <option value="Harvest">Added</option>
              <option value="Bottling">Packaged</option>
              <option value="Correction">Corrected</option>
              <option value="Sale">Sold</option>
              <option value="Loss">Lost</option>
            </select>
          )}
        </Field>
        <Field label="From">
          {(id, aria) => (
            <input id={id} {...aria} type="date" className="touch-target" value={from}
              onChange={(e) => setFrom(e.target.value)} />
          )}
        </Field>
        <Field label="To">
          {(id, aria) => (
            <input id={id} {...aria} type="date" className="touch-target" value={to}
              onChange={(e) => setTo(e.target.value)} />
          )}
        </Field>
      </div>

      {failed ? (
        <p style={{ margin: 0, color: "var(--color-danger)" }}>
          Could not load the activity just now.
        </p>
      ) : (
        <DataTable
          rows={rows === null ? [] : shown}
          loading={rows === null}
          rowKey="movement_id"
          columns={[
            { key: "created_date", header: "When", render: (r) => formatDateOnly(r.created_date) },
            { key: "product_type", header: "Product" },
            { key: "movement_type", header: "What",
              render: (r) => <Badge tone={TYPE_TONE[r.movement_type] || "neutral"}>{whatLabel(r)}</Badge> },
            { key: "detail", header: "Detail", render: detail },
            { key: "reason", header: "Note", render: (r) => r.reason || "" },
          ]}
          emptyTitle="Nothing recorded yet"
          emptyDescription="Everything in and out of these pools appears here."
        />
      )}
      <ListBound cappedAt={cappedAt} noun="movements" filterable />
    </Card>
  );
}
